import {
  btnDanger,
  btnPrimary,
  btnSecondary,
  iconBtn
} from '@/components/record-sheet/styles'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  type ChatRoomSummary,
  archiveRoom,
  deleteRoom,
  renameRoom
} from '@/lib/ai-team'
import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import { Archive, Loader2, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

const BASE = '/apps/ai-team/chat'

/** Rename, archive or delete a chat. The team room has no menu. */
export function ChatRoomMenu({
  room,
  className
}: { room: ChatRoomSummary; className?: string }) {
  const navigate = useNavigate()
  const { roomId } = useParams()
  const [renaming, setRenaming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [title, setTitle] = useState(room.title)
  const [pending, setPending] = useState(false)

  if (room.kind === 'team' || room.kind === 'feed') return null
  const isOpen = Number(roomId) === room.id

  async function run(fn: () => Promise<unknown>, done: string, leave: boolean) {
    setPending(true)
    try {
      await fn()
      if (leave && isOpen) navigate(BASE)
      await queryClient.invalidateQueries({ queryKey: ['ai-rooms'] })
      await queryClient.invalidateQueries({ queryKey: ['ai-room', room.id] })
      toast.success(done)
      setRenaming(false)
      setDeleting(false)
    } catch (err) {
      notifyError('Could not update the chat', err)
    } finally {
      setPending(false)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(iconBtn, 'size-8', className)}
            aria-label={`Options for ${room.title || 'this chat'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44 rounded-[36px]">
          <DropdownMenuItem
            className="gap-2"
            onSelect={() => {
              setTitle(room.title)
              setRenaming(true)
            }}
          >
            <Pencil className="size-4" /> Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-2"
            onSelect={() =>
              run(() => archiveRoom(room.id), 'Chat archived.', true)
            }
          >
            <Archive className="size-4" /> Archive
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="gap-2 text-red-600 focus:text-red-600 dark:text-red-400"
            onSelect={() => setDeleting(true)}
          >
            <Trash2 className="size-4" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={renaming} onOpenChange={(o) => !pending && setRenaming(o)}>
        <DialogContent className="rounded-[36px] border border-neutral-200 bg-white p-6 shadow-2xl sm:max-w-md dark:border-neutral-800 dark:bg-neutral-950">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-neutral-900 dark:text-white">
              Rename chat
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (title.trim())
                run(() => renameRoom(room.id, title.trim()), 'Renamed.', false)
            }}
            className="space-y-4"
          >
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              aria-label="Chat name"
              className="w-full rounded-[36px] border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white"
            />
            <DialogFooter className="gap-2">
              <button
                type="button"
                className={btnSecondary}
                onClick={() => setRenaming(false)}
                disabled={pending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={btnPrimary}
                disabled={pending || !title.trim()}
              >
                {pending && <Loader2 className="size-4 animate-spin" />}
                Save
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleting}
        onOpenChange={(o) => !pending && setDeleting(o)}
      >
        <AlertDialogContent className="rounded-[36px] border border-neutral-200 bg-white p-6 shadow-2xl dark:border-neutral-800 dark:bg-neutral-950">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold text-neutral-900 dark:text-white">
              Delete "{room.title || 'this chat'}"?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-neutral-500 dark:text-neutral-400">
              The messages and steps in this chat are deleted for good. Anything
              waiting for your yes stays in Needs your input.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className={btnSecondary} disabled={pending}>
              Cancel
            </AlertDialogCancel>
            <button
              type="button"
              className={btnDanger}
              disabled={pending}
              onClick={() =>
                run(() => deleteRoom(room.id), 'Chat deleted.', true)
              }
            >
              {pending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Trash2 className="size-4" />
              )}
              Delete
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
